import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { serviceGroups } from "../data/services";

const groups = serviceGroups;

const serviceCount = groups.reduce((total, group) => total + group.items.length, 0);

const STEP_MS = 240;
const LIST_START_MS = 560;
const ROW_MS = 700;

const groupIcons = {
  plans: ["M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"],
  permits: ["M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"],
  site: ["M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z", "M15 11a3 3 0 11-6 0 3 3 0 016 0z"],
  support: ["M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"],
};

function GroupIcon({ name }) {
  const paths = groupIcons[name] || groupIcons.plans;
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-orange-500/15 text-orange-500">
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        {paths.map((d) => (
          <path key={d} strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={d} />
        ))}
      </svg>
    </span>
  );
}

const scheduledGroups = (() => {
  let step = 0;
  let count = 0;
  return groups.map((group) => {
    const headerDelay = LIST_START_MS + step * STEP_MS;
    step += 1;
    const items = group.items.map((item) => {
      count += 1;
      const delay = LIST_START_MS + step * STEP_MS;
      step += 1;
      return {
        ...item,
        delay,
        number: String(count).padStart(2, "0"),
      };
    });
    return { ...group, delay: headerDelay, items };
  });
})();

function revealClass(started) {
  return started
    ? "grid-rows-[1fr] opacity-100"
    : "grid-rows-[0fr] opacity-0 motion-reduce:grid-rows-[1fr] motion-reduce:opacity-100";
}

const loadMarks = (() => {
  let cursor = 0;
  return scheduledGroups.map((group) => {
    const startAt = cursor;
    const doneAt = group.items[group.items.length - 1].delay + ROW_MS;
    cursor = doneAt;
    return { title: group.title, startAt, doneAt, duration: doneAt - startAt };
  });
})();

export default function CapabilitiesSection() {
  const sectionRef = useRef(null);
  const revealStartedAt = useRef(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !sectionRef.current) {
      setStarted(true);
      return;
    }
    if (!("IntersectionObserver" in window)) {
      setStarted(true);
      return;
    }

    const node = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStarted(true);
        observer.disconnect();
      },
      { threshold: 0.2 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started || !sectionRef.current) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!revealStartedAt.current) revealStartedAt.current = performance.now();

    const rows = [...sectionRef.current.querySelectorAll("[data-step]")]
      .map((el) => ({ el, delay: Number(el.dataset.step) }))
      .sort((a, b) => a.delay - b.delay);
    if (!rows.length) return;

    const endAt = rows[rows.length - 1].delay + ROW_MS;
    let frame = 0;
    let stopped = false;
    const origin = revealStartedAt.current;

    const stop = () => {
      stopped = true;
    };
    const onWheel = (event) => {
      if (event.deltaY < -8) stop();
    };
    const onKey = (event) => {
      if (event.key === "ArrowUp" || event.key === "PageUp" || event.key === "Home") stop();
    };

    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    window.addEventListener("keydown", onKey);

    const tick = () => {
      if (stopped) return;
      const elapsed = performance.now() - origin;
      let active = rows[0];
      for (const row of rows) {
        if (row.delay <= elapsed) active = row;
        else break;
      }
      const bottom = active.el.getBoundingClientRect().bottom;
      const delta = bottom - window.innerHeight * 0.72;
      if (delta > 4) window.scrollBy(0, Math.min(delta * 0.22, 24));
      if (elapsed < endAt) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("keydown", onKey);
    };
  }, [started, sectionRef]);

  return (
    <section
      ref={sectionRef}
      id="capabilities"
      className="scroll-mt-28 bg-white py-20 lg:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <p className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-500">
                <span className="h-px w-8 bg-orange-500" />
                What we do
              </p>
              <h2 className="text-3xl font-extrabold tracking-tight text-black sm:text-4xl lg:text-[2.6rem] lg:leading-[1.1]">
                Our Capabilities
              </h2>
              <p className="mt-5 text-[15px] leading-relaxed text-gray-500">
                Plans, permits, and site work for construction, events, and road
                projects. Available 24/7, with the right people pulled in when
                the job needs them.
              </p>
              <p className="mt-6 text-sm font-medium text-gray-800">
                <span className="text-orange-500">{serviceCount}+</span> services
                <span className="mx-2 text-gray-300">/</span>
                <span className="text-orange-500">24/7</span> availability
              </p>
              <Link
                to="/contact"
                className="group mt-8 inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3.5 text-sm font-semibold uppercase tracking-wide text-white shadow-lg shadow-orange-500/25 transition hover:bg-orange-600"
              >
                Talk to an expert
                <svg
                  className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="rounded-3xl bg-[#121212] px-5 py-6 text-white sm:px-8 sm:py-8">
              <div className="mb-5 grid grid-cols-4 gap-2" role="group" aria-label="Capabilities loading">
                {loadMarks.map((mark) => (
                  <div key={mark.title}>
                    <p
                      className={`mb-1.5 flex h-8 items-end justify-center gap-1 text-center text-[10px] font-bold uppercase leading-tight tracking-[0.08em] transition-colors duration-300 motion-reduce:text-orange-500 motion-reduce:transition-none sm:tracking-[0.12em] ${
                        started ? "text-orange-500" : "text-white/35"
                      }`}
                      style={{ transitionDelay: started ? `${mark.doneAt}ms` : undefined }}
                    >
                      {mark.title}
                      <svg
                        className={`h-3 w-3 shrink-0 transition-opacity duration-300 motion-reduce:opacity-100 motion-reduce:transition-none ${
                          started ? "opacity-100" : "opacity-0"
                        }`}
                        style={{ transitionDelay: started ? `${mark.doneAt}ms` : undefined }}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.7 5.3a1 1 0 010 1.4l-7.2 7.2a1 1 0 01-1.4 0L3.3 9.1a1 1 0 011.4-1.4l4.1 4.1 6.5-6.5a1 1 0 011.4 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </p>
                    <div className="h-1 overflow-hidden rounded-full bg-white/10">
                      <div
                        className="h-full origin-left rounded-full bg-orange-500 motion-reduce:scale-x-100 motion-reduce:transition-none"
                        style={{
                          transform: started ? "scaleX(1)" : "scaleX(0)",
                          transition: started
                            ? `transform ${mark.duration}ms linear ${mark.startAt}ms`
                            : "none",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {scheduledGroups.map((group, groupIndex) => (
                <div key={group.title}>
                  <div
                    data-step={group.delay}
                    className={`grid transition-all duration-700 ease-out motion-reduce:transition-none ${revealClass(started)}`}
                    style={{ transitionDelay: started ? `${group.delay}ms` : undefined }}
                  >
                    <div className="overflow-hidden">
                      <div className={`flex items-center gap-3 pb-1 ${groupIndex === 0 ? "pt-1" : "pt-6"}`}>
                        <GroupIcon name={group.icon} />
                        <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-orange-500">
                          {group.title}
                        </h3>
                        <span
                          className={`h-px flex-1 origin-left bg-white/15 transition-transform duration-700 ease-out motion-reduce:scale-x-100 motion-reduce:transition-none ${
                            started ? "scale-x-100" : "scale-x-0"
                          }`}
                          style={{ transitionDelay: started ? `${group.delay}ms` : undefined }}
                        />
                      </div>
                    </div>
                  </div>
                  <ul>
                    {group.items.map((item) => (
                      <li
                        key={item.slug}
                        data-step={item.delay}
                        className={`grid transition-all duration-700 ease-out motion-reduce:transition-none ${revealClass(started)}`}
                        style={{ transitionDelay: started ? `${item.delay}ms` : undefined }}
                      >
                        <div className="overflow-hidden">
                          <Link
                            to={item.to}
                            className="group flex items-center gap-4 border-b border-white/10 py-3"
                          >
                            <span className="w-7 shrink-0 text-xs font-semibold tabular-nums text-orange-500">
                              {item.number}
                            </span>
                            <span className="text-sm leading-snug text-white/85 transition-colors group-hover:text-white">
                              {item.label}
                            </span>
                            <svg
                              className="ml-auto h-4 w-4 shrink-0 text-white/25 transition duration-300 group-hover:translate-x-0.5 group-hover:text-orange-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </Link>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
